import { Button } from "@mui/material";
import clsx from "clsx";
import dayjs from "dayjs";
import React, { useContext, useEffect, useRef, useState } from "react";
import { HTMLGridPanelElement } from "../../../ComponentInterface";
import Editor from "../../../Editor/Editor";
import smetaAxios from "../../../SmetaAxios";
import { tokenProcessingTest } from "../../../TokenProcessing/TokenProcessing";
import ContextMenu from "../../NotWorkArea(Side&Head)/ContextMenu";
import { GridLoader } from "../../rcsgrid/createGridTools";
import { DocFieldType, ObjType } from "../../Tools/Consts";
import { smetaModals, SmetaModalsIdContext } from "../../Tools/modalManager";
import { EditStyle, Prefixes } from "../../Tools/ProjectX";
import { getParamObjectText, getParamObjectValues, setParamObjectText } from "../../Tools/Requests";
import { getDateOfInt, getIntOfStrDate, getStyleWindow } from "../../Tools/Tools";
import dialogStyles from "./Components/dialogStyles.module.css";
import DialogSetObjText from "./DialogSetObjText";

interface GetDepNameHistory {
  ObjRef?: string;
  Text?: string;
  Name?: string;
  Title?: string;
  Items?: { items: { UsedDate: string; Name?: string; Title?: string }[] };
}

interface RecordItem {
  UsedDate: { id: string; text: string };
  MainName: string | undefined;
  FullName: string | undefined;
}

const defaultColumns = {
  title: [
    {
      fieldName: "UsedDate",
      title: "Дата",
      width: 100,
      fieldInfo: {
        DataType: DocFieldType.IntDate,
        EditStyle: EditStyle.Calendar,
      },
    },
    {
      fieldName: "MainName",
      title: "Основное название",
      width: 300,
      fieldInfo: { DataType: DocFieldType.String },
    },
    {
      fieldName: "FullName",
      title: "Полное наименование",
      width: 300,
      fieldInfo: { DataType: DocFieldType.String },
    },
  ],
};
const defaultFields = [
  {
    Name: "UsedDate",
    Title: "Дата",
    Width: "10",
  },
  {
    Name: "MainName",
    Title: "Основное название",
    Width: "7",
  },
  {
    Name: "FullName",
    Title: "Полное наименование",
    Width: "20",
  },
];

// eslint-disable-next-line @typescript-eslint/naming-convention
export default function DialogDepNames({ departsId, onSelect }: { departsId: string | number; onSelect?: () => void }) {
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [objRef, setObjRef] = useState("");
  const [title, setTitle] = useState("");
  const gridPanelRef = useRef<HTMLGridPanelElement>(null!);
  const records = useRef<RecordItem[]>([]);
  const datasetId = useContext(SmetaModalsIdContext);

  useEffect(() => {
    smetaAxios
      .get<GetDepNameHistory>("GetDepNameHistory", { prefix: Prefixes.deps, params: { ID: departsId }, loadMask: true })
      .then((json) => {
        if (json.Name) setName(json.Name);
        if (json.Text) setText(json.Text);
        if (json.ObjRef) setObjRef(json.ObjRef);
        if (json.Title) setTitle(json.Title);
        if (json.Items?.items) {
          records.current = json.Items.items.map((item) => {
            return {
              UsedDate: { id: item.UsedDate, text: item.UsedDate !== "0" ? getDateOfInt(item.UsedDate) : "" },
              MainName: item.Name,
              FullName: item.Title,
            };
          });
        }
        mainGridLoader();
      });
  }, []);

  const getFieldText = async (request: { FieldName: string }) => {
    return { Text: await gridPanelRef.current.grid.source.getFieldText(request.FieldName) };
  };

  const onPostRecord = (request, requestData) => {
    if (request.NewRecord === "1") {
      const newRecord: RecordItem = {
        UsedDate: { id: "", text: "" },
        MainName: undefined,
        FullName: undefined,
      };
      request.RecordSet.Attributes.forEach((attribut, index) => {
        newRecord[attribut] = attribut === "UsedDate" ? requestData[index] : requestData[index].text;
      });
      records.current.splice(request.RecordIndex, 0, newRecord);
      return;
    }
    const modifiedField = request.RecordSet.Attributes[0];
    const record = records.current[request.RecordIndex];
    record[modifiedField] = modifiedField === "UsedDate" ? requestData[0] : requestData[0].text;
  };

  const createRecord = () => {
    const index = records.current.length ? gridPanelRef.current.grid.source.activeRecord + 1 : 0;
    const newRecords = [...records.current];
    const usedDateStr = dayjs().startOf("month").format("DD.MM.YYYY");
    const newRecord: RecordItem = {
      UsedDate: { id: String(getIntOfStrDate(usedDateStr)), text: usedDateStr },
      MainName: undefined,
      FullName: undefined,
    };
    newRecords.splice(index, 0, newRecord);
    return {
      index: index,
      records: newRecords,
      keys: Object.keys(newRecord),
      titleKey: "",
    };
  };

  const deleteRecord = (request) => {
    if (!records.current.length) return;
    records.current.splice(request.RecordIndex, 1);
    return {
      Command: "DeleteRecord",
      RecordIndex: records.current.length === 0 ? 0 : request.RecordIndex - 1,
      RecordCount: records.current.length,
      UpdatesPending: "1",
      RecordKey: {},
    };
  };

  async function mainGridLoader() {
    GridLoader(gridPanelRef.current, undefined, {
      localGrid: true,
      refresh: false,
      showIndicator: false,
      Columns: defaultColumns,
      defaultFields,
      keyField: "UsedDate",
      recordKeys: ["UsedDate", "MainName", "FullName"],
      titleKey: "UsedDate",
      getFieldText,
      onPostRecord,
      createRecord,
      deleteRecord,
      getRecords: () => records.current,
      Rights: "-1",
    });
  }

  function setDepNameHistory() {
    const data = records.current.map((item) => {
      return { UsedDate: item.UsedDate.id, Name: item.MainName, Title: item.FullName };
    });
    return smetaAxios.post(
      "SetDepNameHistory",
      { Items: data.length ? data : undefined },
      { prefix: Prefixes.deps, params: { ID: departsId, ObjRef: objRef, Name: name, Title: title }, loadMask: true },
    );
  }

  async function organizationDropDownList() {
    const json = await getParamObjectValues(Prefixes.orgs, ObjType.Organization);
    return json.Items.map((item) => {
      return { label: item.text, id: item.id };
    });
  }

  function organizationOnEditButtonClick(editButtonData) {
    tokenProcessingTest(editButtonData.json, editButtonData.subData);
  }

  async function organizationOnEdit(data: { tag: string; textchanged: string; value: string }) {
    const isTextChanged = data.textchanged === "1";
    if (!isTextChanged) {
      setObjRef(data.tag);
      const newTextValue = (await getParamObjectText(Prefixes.orgs, ObjType.Organization, data.tag, -1)).Text ?? "";
      setText(newTextValue);
      return;
    }
    if (!data.value) {
      setObjRef("");
      setText("");
      return;
    }

    const json = await setParamObjectText(Prefixes.orgs, ObjType.Organization, objRef, data.value);
    if (json.ObjRef === "-1") {
      if (json.Selection?.length) {
        smetaModals.open(<DialogSetObjText list={json.Selection} onEdit={organizationOnEdit} />, {
          Title: "Выберите элемент из списка",
          style: getStyleWindow(),
          hiddenButton: true,
          blockMaximize: true,
        });
      }
      return;
    }
    setObjRef(json.ObjRef);
    setText(json.Text);
  }

  const handleClose = () => {
    smetaModals.close(datasetId);
  };

  const handleSet = async () => {
    await setDepNameHistory();
    await smetaModals.close(datasetId);
    onSelect?.();
  };

  return (
    <div className={clsx(dialogStyles.mainDialogBlock)}>
      <div className={clsx(dialogStyles.dialogDepNamesEditorBlock)}>
        <div className={clsx(dialogStyles.dialogDepNamesLabel)}>Присоединенная организация:</div>
        <div className={clsx(dialogStyles.dialogDepNamesEditorContainer)}>
          <Editor
            editStyle={EditStyle.PickList + EditStyle.Ellipsis}
            value={text}
            objref={objRef}
            onDropDownList={organizationDropDownList}
            datatype={ObjType.Organization}
            type={Prefixes.orgs}
            onEditButtonClick={organizationOnEditButtonClick}
            onEdit={organizationOnEdit}
          />
        </div>
      </div>
      <div className={clsx(dialogStyles.dialogDepNamesEditorBlock)}>
        <div className={clsx(dialogStyles.dialogDepNamesLabel)}>Название по умолчанию:</div>
        <div className={clsx(dialogStyles.dialogDepNamesEditorContainer)}>
          <Editor
            value={name}
            onEdit={(data: { value: string }) => {
              setName(data.value);
            }}
          />
        </div>
      </div>
      <div className={clsx(dialogStyles.dialogDepNamesEditorBlock)}>
        <div className={clsx(dialogStyles.dialogDepNamesLabel)}>Полное наименование:</div>
        <div className={clsx(dialogStyles.dialogDepNamesEditorContainer)}>
          <Editor
            value={title}
            onEdit={(data: { value: string }) => {
              setTitle(data.value);
            }}
          />
        </div>
      </div>
      <div className={clsx(dialogStyles.dialogDepNamesEditorBlock)}>
        <div className={clsx(dialogStyles.dialogDepNamesLabel)}>Название на дату</div>
        <div className={clsx(dialogStyles.dialogDepNamesDivider)} />
      </div>
      <div className={clsx(dialogStyles.dialogDepNamesGridButtonContainer)}>
        <div className={clsx(dialogStyles.dialogDepNamesGridContainer)}>
          <ContextMenu for="grid">
            <div ref={gridPanelRef} style={{ width: "100%", height: "100%", position: "relative" }} rcsgrid_panel="" />
          </ContextMenu>
        </div>
        <div className={clsx(dialogStyles.dialogDepNamesButtonContainer)}>
          <Button size="small" variant="outlined" onClick={handleSet}>
            Установить
          </Button>
          <Button size="small" variant="outlined" onClick={handleClose}>
            Закрыть
          </Button>
        </div>
      </div>
    </div>
  );
}
